/**
 * useLanguage React hook
 *
 * Manages the application's language state, toggling between English ('en') and Spanish ('es').
 * Handles side effects such as updating the HTML document's lang attribute, localStorage,
 * analytics configuration, OneTrust consent manager, and Qualtrics survey language.
 *
 * @component
 * @returns {{ currentLanguage: string, handleLanguageChange: () => void }}
 *   currentLanguage - The current language code ('en' or 'es')
 *   handleLanguageChange - Function to toggle and update language
 */

import { useEffect, useState } from 'react'
import { getLanguageFromStorage } from 'helpers'
import setLanguageFromQueryParams from 'helpers/setLanguageFromQueryParams'
import triggerAnalyticsEvent, {
  setAnalyticsDefaults
} from 'helpers/triggerAnalyticsEvent'
import triggerQualtrics from 'helpers/triggerQualtrics'

export function useLanguage() {
  const [currentLanguage, setCurrentLanguage] = useState(
    getLanguageFromStorage()
  )

  useEffect(() => {
    // Set language from query params if present (e.g., ?lang=es)
    setLanguageFromQueryParams()
    // Set the HTML document's lang attribute for accessibility and SEO
    document.documentElement.setAttribute('lang', currentLanguage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Toggle the language between English ('en') and Spanish ('es'),
   * update storage, document, analytics, OneTrust, and Qualtrics.
   */
  const handleLanguageChange = () => {
    const newLanguage = currentLanguage === 'en' ? 'es' : 'en'
    localStorage.setItem('language', newLanguage)

    document.documentElement.setAttribute('lang', newLanguage)

    setCurrentLanguage(newLanguage)

    setAnalyticsDefaults({ Country_Language: `us|${newLanguage}` })
    triggerAnalyticsEvent({ Country_Language: `us|${newLanguage}` })

    window.OneTrust?.changeLanguage()
    triggerQualtrics()
  }

  return { currentLanguage, handleLanguageChange }
}
